import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MarkSheet, RankedStudentRow, StudentScoreRow } from '../models/grades.model';

const LS_MARK_SHEETS = 'eschool-grades-mark-sheets-v1';

/**
 * Marks, percentage grades, and class ranking.
 *
 * Suggested Spring API:
 * - GET/PUT `/api/v1/grades/mark-sheets` → `MarkSheet[]`
 */
@Injectable({
  providedIn: 'root'
})
export class GradesService {

  private readonly base = `${environment.apiUrl}/api/v1/grades`;

  constructor(private http: HttpClient) { }

  getMarkSheets(): Observable<MarkSheet[]> {
    return this.http.get<MarkSheet[]>(`${this.base}/mark-sheets`).pipe(
      tap(s => this.cacheSheets(s)),
      catchError(() => of(this.readSheets()))
    );
  }

  saveMarkSheets(sheets: MarkSheet[]): Observable<void> {
    window.localStorage.setItem(LS_MARK_SHEETS, JSON.stringify(sheets));
    return this.http.put<void>(`${this.base}/mark-sheets`, sheets).pipe(
      catchError(() => of(undefined))
    );
  }

  newSheetId(): string {
    return `ms-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  newColumnId(): string {
    return `col-${Math.random().toString(36).slice(2, 10)}`;
  }

  letterGrade(percent: number): string {
    if (percent >= 90) {
      return 'A+';
    }
    if (percent >= 80) {
      return 'A';
    }
    if (percent >= 70) {
      return 'B';
    }
    if (percent >= 60) {
      return 'C';
    }
    if (percent >= 50) {
      return 'D';
    }
    return 'F';
  }

  rowTotals(sheet: MarkSheet, row: StudentScoreRow): { obtained: number; max: number; percent: number } {
    let obtained = 0;
    let max = 0;
    for (const col of sheet.columns) {
      max += col.maxMarks;
      const raw = row.scores[col.id];
      const v = typeof raw === 'number' && !Number.isNaN(raw) ? raw : 0;
      obtained += Math.min(Math.max(0, v), col.maxMarks);
    }
    const percent = max > 0 ? (obtained / max) * 100 : 0;
    return { obtained, max, percent };
  }

  /** Competition-style ranks: tied students share rank; next distinct score skips ranks. */
  rankStudents(sheet: MarkSheet): RankedStudentRow[] {
    const enriched: RankedStudentRow[] = sheet.rows.map(row => {
      const { obtained, max, percent } = this.rowTotals(sheet, row);
      return {
        ...row,
        scores: { ...row.scores },
        totalObtained: obtained,
        totalMax: max,
        percent,
        letterGrade: this.letterGrade(percent),
        rank: 0
      };
    });

    enriched.sort((a, b) => {
      if (b.percent !== a.percent) {
        return b.percent - a.percent;
      }
      if (b.totalObtained !== a.totalObtained) {
        return b.totalObtained - a.totalObtained;
      }
      const na = `${a.lastName} ${a.firstName}`.toLowerCase();
      const nb = `${b.lastName} ${b.firstName}`.toLowerCase();
      return na.localeCompare(nb);
    });

    for (let i = 0; i < enriched.length; i++) {
      if (i === 0) {
        enriched[i].rank = 1;
      } else {
        const prev = enriched[i - 1];
        const cur = enriched[i];
        if (cur.percent === prev.percent && cur.totalObtained === prev.totalObtained) {
          cur.rank = prev.rank;
        } else {
          cur.rank = i + 1;
        }
      }
    }

    return enriched;
  }

  private cacheSheets(sheets: MarkSheet[]): void {
    if (sheets && sheets.length >= 0) {
      window.localStorage.setItem(LS_MARK_SHEETS, JSON.stringify(sheets));
    }
  }

  private readSheets(): MarkSheet[] {
    const raw = window.localStorage.getItem(LS_MARK_SHEETS);
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw) as MarkSheet[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}
